import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { CheckCircle2, XCircle, Loader2, Download, ArrowRight } from "lucide-react";

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("checking"); // checking, success, failed
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === "paid") {
          setStatus("success");
        } else if (response.data.status === "expired") {
          setStatus("failed");
        } else if (attempts < 5) {
          // Keep polling
          setTimeout(() => setAttempts((a) => a + 1), 2000);
        } else {
          setStatus("failed");
        }
      } catch (e) {
        if (attempts < 5) {
          setTimeout(() => setAttempts((a) => a + 1), 2000);
        } else {
          setStatus("failed");
        }
      }
    };

    checkStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-background noise-bg" data-testid="checkout-success-page">
      <Navbar />

      <main className="pt-32 pb-16 container-padding">
        <div className="max-w-lg mx-auto text-center">
          {status === "checking" && (
            <div className="animate-fade-in">
              <div className="w-20 h-20 mx-auto mb-6 bg-zinc-100 rounded-full flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-indigo-500 spinner" />
              </div>
              <h1 
                className="text-2xl font-semibold mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Processing Your Payment...
              </h1>
              <p className="text-zinc-600">
                Please wait while we confirm your payment.
              </p>
            </div>
          )}

          {status === "success" && (
            <div className="animate-fade-in-up">
              <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-green-500" />
              </div>
              <h1 
                className="text-3xl font-semibold mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Payment Successful!
              </h1>
              <p className="text-zinc-600 mb-8">
                Thank you for your purchase. Your designs are now available in your dashboard.
              </p>

              <div className="bg-white p-6 rounded-xl border border-zinc-200 mb-8">
                <h3 className="font-medium mb-4">What's Next?</h3>
                <ul className="space-y-3 text-left">
                  <li className="flex items-start gap-3">
                    <Download className="w-5 h-5 text-indigo-500 mt-0.5" />
                    <span className="text-sm text-zinc-600">
                      Download your high-resolution design files from your dashboard
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
                    <span className="text-sm text-zinc-600">
                      Commercial license included - use on any POD platform
                    </span>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/dashboard" 
                  className="btn-primary flex items-center justify-center gap-2"
                  data-testid="go-to-dashboard"
                >
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link 
                  to="/marketplace" 
                  className="btn-secondary flex items-center justify-center gap-2"
                >
                  Browse More Designs
                </Link>
              </div>
            </div>
          )}

          {status === "failed" && (
            <div className="animate-fade-in-up">
              <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 
                className="text-3xl font-semibold mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Payment Failed
              </h1>
              <p className="text-zinc-600 mb-8">
                We couldn't process your payment. Please try again or contact support if the issue persists.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/cart" 
                  className="btn-primary"
                  data-testid="try-again"
                >
                  Try Again
                </Link>
                <Link to="/marketplace" className="btn-secondary">
                  Back to Marketplace
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
